import { useState } from 'react'

import { МАСШТАБЫ, ПРИСТАВКИ, ru, withPrefix } from '../../lib/physics'
import { Slider, useFrames } from './common'

/**
 * Лестница масштабов (урок 3): от атома до расстояния до Солнца. Метры для атома и для
 * Луны неудобны — поэтому есть десятичные приставки. Указатель плавно едет по лестнице
 * степеней десяти.
 */

const МИН = -10
const МАКС = 12
const ШИР = 320
const X0 = 20

const x = (степень: number) => X0 + ((степень - МИН) / (МАКС - МИН)) * ШИР

const ВЕРХНИЕ: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' }
/** 10 в степени — верхним индексом: 10⁻¹⁰. */
const степень10 = (n: number) => '10' + String(n).split('').map((c) => ВЕРХНИЕ[c] ?? c).join('')

export default function ScaleLab() {
  const [n, setN] = useState(7) // номер объекта
  const [вид, setВид] = useState(Math.log10(МАСШТАБЫ[7].размер))

  const объект = МАСШТАБЫ[n]
  const цель = Math.log10(объект.размер)
  useFrames((dt) => setВид((v) => v + (цель - v) * Math.min(1, dt * 6)))

  const { число, приставка } = withPrefix(объект.размер)
  const порядок = Math.floor(Math.log10(объект.размер) + 1e-9)
  const вМетрах = объект.размер >= 1 && объект.размер < 1e6
    ? `${ru(объект.размер, объект.размер < 10 ? 1 : 0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} м`
    : `${ru(объект.размер / 10 ** порядок, 1)} · ${степень10(порядок)} м`

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 150" role="img" aria-label="Лестница масштабов">
        <line x1={X0} y1={80} x2={X0 + ШИР} y2={80} className="ink" strokeWidth={2} />
        {Array.from({ length: МАКС - МИН + 1 }, (_, i) => МИН + i).map((с) => (
          <g key={с}>
            <line x1={x(с)} y1={74} x2={x(с)} y2={с % 3 === 0 ? 90 : 84} className="ink" />
            {с % 3 === 0 && <text x={x(с)} y={104} textAnchor="middle" className="lab-tick">{степень10(с)}</text>}
          </g>
        ))}
        {ПРИСТАВКИ.map((п) => (
          <text key={п.степень} x={x(п.степень)} y={124} textAnchor="middle" className={п.степень === приставка.степень ? 'lab-label el' : 'lab-tick'}>
            {п.обозначение ? `${п.обозначение}м` : 'м'}
          </text>
        ))}
        {МАСШТАБЫ.map((о, i) => (
          <circle key={о.что} cx={x(Math.log10(о.размер))} cy={80} r={i === n ? 0 : 3} className="fill-muted" />
        ))}
        <g transform={`translate(${x(вид)} 0)`}>
          <polygon points="0,72 -7,58 7,58" className="fill-heavy" />
          <text x={0} y={50} textAnchor="middle" className="lab-label heavy">{объект.что}</text>
        </g>
        <text x={X0} y={144} className="lab-tick">приставки к метру</text>
      </svg>

      <div className="lab-big">
        {объект.что}: <b>{ru(число, число < 10 ? 1 : 0)} {приставка.обозначение}м</b>
      </div>
      <div className="lab-readout lab-readout-col">
        <span>в метрах: {вМетрах}</span>
        <span>
          {приставка.приставка
            ? `«${приставка.приставка}» — в ${степень10(Math.abs(приставка.степень))} раз ${приставка.степень > 0 ? 'больше' : 'меньше'} метра`
            : 'для таких размеров удобен сам метр'}
        </span>
      </div>

      <Slider
        label="Что измеряем"
        value={n}
        min={0}
        max={МАСШТАБЫ.length - 1}
        step={1}
        unit=""
        shown={объект.что}
        onChange={setN}
      />
      <table className="lab-table">
        <thead>
          <tr><th>приставка</th><th>обозначение</th><th>множитель</th></tr>
        </thead>
        <tbody>
          {ПРИСТАВКИ.filter((п) => п.приставка).map((п) => (
            <tr key={п.степень} className={п.степень === приставка.степень ? 'lab-row-on' : ''}>
              <td>{п.приставка}</td>
              <td>{п.обозначение}</td>
              <td>{степень10(п.степень)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">
        Размеры атома неудобно писать в метрах, расстояние до Луны — тоже. Десятичные приставки
        делают числа удобными: кило — тысяча, мега — миллион, милли — тысячная, нано — миллиардная.
      </p>
    </div>
  )
}
